import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { ArrowRight, Search, X, Sparkles, Loader2 } from "lucide-react";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { slug: "about", label: "About DISJINX" },
  { slug: "plans", label: "Plans & Pricing" },
  { slug: "billing", label: "Billing & Payments" },
  { slug: "support", label: "Support & Service" },
  { slug: "privacy", label: "Privacy & Information" },
  { slug: "contact", label: "Contact" },
];

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const BulletList = ({ items }) => (
  <ul className="list-disc space-y-1.5 pl-5">
    {items.map((i) => (
      <li key={i}>{i}</li>
    ))}
  </ul>
);

const faqs = [
  {
    cat: "about",
    t: "AI automation agency helps businesses automate repetitive time-consuming tasks reduce manual work save time efficiency focus customers team",
    q: "What does DISJINX do?",
    a: (
      <div className="space-y-3">
        <p>DISJINX is an AI automation agency that helps Australian businesses automate repetitive and time-consuming tasks.</p>
        <p>We design and implement AI-powered workflows that reduce manual work, save valuable time and help businesses operate more efficiently.</p>
        <p>Our goal is simple: to take busywork off your plate so you can focus on your customers, your team and the parts of your business that matter most.</p>
      </div>
    ),
  },
  {
    cat: "about",
    t: "automation definition meaning automatic system tasks example customer enquiry analyse AI database notify team",
    q: "What is an automation?",
    a: (
      <div className="space-y-3">
        <p>An automation is a system that allows tasks to happen automatically without requiring someone to manually complete each step.</p>
        <p>For example, an automation could receive a customer enquiry, analyse the message using AI, save the information to your database and notify your team — all without requiring someone to manually complete these tasks.</p>
      </div>
    ),
  },
  {
    cat: "about",
    t: "step individual action within automation workflow example receive analyse save respond notify complexity",
    q: "What is a step in an automation?",
    a: (
      <div className="space-y-3">
        <p>A step is an individual action that takes place within an automation. Each step performs a specific task as part of the overall workflow.</p>
        <p>For example, an automation could:</p>
        <ol className="list-decimal space-y-1.5 pl-5">
          <li>Receive a customer enquiry</li>
          <li>Analyse the enquiry using AI</li>
          <li>Save the customer's information</li>
          <li>Send a response to the customer</li>
          <li>Notify a member of your team</li>
        </ol>
        <p>Each of these actions would represent a step in automation. The number of steps required depends on what you want the automation to accomplish and how complex the workflow is.</p>
      </div>
    ),
  },
  {
    cat: "about",
    t: "app connection software application service automation connects website AI google sheets depends on plan",
    q: "What is an app connection?",
    a: (
      <div className="space-y-3">
        <p>An app connection refers to a software application or service that your automation connects to in order to perform its tasks.</p>
        <p>For example, an automation that receives information from a website, processes it with AI and saves it to Google Sheets would use multiple app connections.</p>
        <p>The number of app connections available depends on your plan.</p>
      </div>
    ),
  },
  {
    cat: "about",
    t: "automate examples customer enquiries email processing data entry lead management appointment workflows notifications document processing AI-powered customer support chatbots",
    q: "What can DISJINX automate?",
    a: (
      <div className="space-y-3">
        <p>DISJINX can automate a wide range of repetitive business tasks. Examples include customer enquiries, email processing, data entry, lead management, appointment workflows, notifications, document processing and AI-powered customer support, including AI-powered chatbots.</p>
        <p>The exact automation depends on your business, your requirements and the systems you already use.</p>
      </div>
    ),
  },
  {
    cat: "about",
    t: "AI chatbots custom built answer questions provide business information assist customer enquiries connect other automations workflows trigger collect information pass to another system capabilities requirements",
    q: "Does DISJINX build AI chatbots?",
    a: (
      <div className="space-y-3">
        <p>Yes. DISJINX can design and build AI chatbots for businesses.</p>
        <p>Our chatbots can be designed to answer customer questions, provide information about your business, assist with enquiries and perform other tasks depending on your requirements.</p>
        <p>AI chatbots can also be connected to other business automations, allowing them to do more than simply answer questions. For example, a chatbot could collect information from a customer and pass it to another system or trigger an automated workflow.</p>
        <p>The capabilities of each chatbot depend on your business requirements and the systems it needs to work with.</p>
      </div>
    ),
  },
  {
    cat: "about",
    t: "technical knowledge AI experience coding skills required none needed we handle technical side",
    q: "Do I need technical or AI knowledge?",
    a: "No. DISJINX handles the technical side of designing, building and maintaining your automations. We work with you to understand your business and identify opportunities where automation can reduce repetitive work.",
  },
  {
    cat: "about",
    t: "process steps consultation planning build launch maintain setup fee non-refundable weekly subscription begins",
    q: "How does the DISJINX process work?",
    a: (
      <div className="space-y-4">
        <p>If you purchase/enquire about an automation, here's what happens next:</p>
        <ol className="list-decimal space-y-2 pl-5">
          <li><strong className="font-semibold text-neutral-950">Consultation</strong> — We learn about your business and identify repetitive tasks that could benefit from automation.</li>
          <li><strong className="font-semibold text-neutral-950">Planning</strong> — We determine the best automation approach for your needs and present our proposed solution to you.</li>
          <li><strong className="font-semibold text-neutral-950">Build</strong> — Once the plan is agreed upon, we create and test your automation. Once this stage has begun, your setup fee is no longer refundable.</li>
          <li><strong className="font-semibold text-neutral-950">Launch</strong> — Your automation is implemented into your workflow and switched on. This is when your weekly subscription begins.</li>
          <li><strong className="font-semibold text-neutral-950">Maintain</strong> — We monitor your automation and make necessary fixes or adjustments according to your plan.</li>
        </ol>
      </div>
    ),
  },
  {
    cat: "about",
    t: "founder Kalel McDonald founded who started",
    q: "Who founded DISJINX?",
    a: "The founder of DISJINX is Kalel McDonald.",
  },
  {
    cat: "plans",
    t: "cost price weekly billed plans essential advanced pro setup fee AUD 49 79 149 99 199",
    q: "How much do the automations cost at DISJINX?",
    a: (
      <div className="space-y-3">
        <p>Our automation plans are billed weekly. All prices are in AUD:</p>
        <BulletList
          items={[
            "Essential — $49 setup + $49/week",
            "Advanced — $99 setup + $79/week",
            "Pro — $199 setup + $149/week",
          ]}
        />
      </div>
    ),
  },
  {
    cat: "plans",
    t: "plan differences features steps app connections usage fee credit rate support priority monitoring maintenance hidden fees contracts setup fee refundable development begins custom plan still unsure",
    q: "What are the differences in automation plans at DISJINX?",
    a: (
      <div className="space-y-5">
        <div className="space-y-3">
          <p>We offer three automation plans, each designed to suit different levels of automation complexity. All plans include ongoing support and weekly automation monitoring and maintenance.</p>
          <p>No hidden fees or long-term contracts. Your only additional charge is usage, which is billed according to the clearly stated credit rate for your plan.</p>
          <p>Each plan has a one-time setup fee.*</p>
        </div>
        <div>
          <p className="font-semibold text-neutral-950">Essential — $49/week</p>
          <BulletList items={["Up to 3 steps", "Up to 3 app connections", "Usage fee: $0.01 per credit", "$49 setup fee"]} />
        </div>
        <div>
          <p className="font-semibold text-neutral-950">Advanced — $79/week</p>
          <BulletList items={["Up to 7 steps", "Up to 5 app connections", "Usage fee: $0.005 per credit", "$99 setup fee"]} />
        </div>
        <div>
          <p className="font-semibold text-neutral-950">Pro — $149/week</p>
          <BulletList items={["Up to 15 steps", "Unlimited app connections", "Usage fee: $0.003 per credit", "Priority support", "$199 setup fee"]} />
        </div>
        <div className="space-y-2">
          <p>If none of these exactly suit your needs, you can enquire about a custom plan at disjinx.support@gmail.com or through our contact page.</p>
          <p>Still unsure? Send us a message at disjinx.support@gmail.com and we'll figure out what plan best suits your needs.</p>
          <p className="text-sm text-neutral-500">*The setup fee covers the consultation, automation design, development and testing of your automation. If you decide not to proceed before development begins, your setup fee will be fully refunded. Once development has begun, the setup fee is non-refundable.</p>
        </div>
      </div>
    ),
  },
  {
    cat: "plans",
    t: "purchase more than one automation multiple plans one account mixing plans multiples same plan essential advanced example",
    q: "Can I purchase more than one automation?",
    a: (
      <div className="space-y-3">
        <p>Yes. Each plan covers one automation, and you can purchase multiple plans under the same account.</p>
        <p>You can choose different plans depending on the requirements of each automation. For example, if you need one 3-step automation and one 7-step automation, you could purchase an Essential plan for the 3-step automation and an Advanced plan for the 7-step automation.</p>
        <p>If you need two 3-step automations, you can simply purchase two Essential plans.</p>
        <p>If you're unsure which combination of plans is best suited to your requirements, contact DISJINX and we'll help you determine the most suitable option.</p>
      </div>
    ),
  },
  {
    cat: "plans",
    t: "none of the plans suit custom plans tailored more steps app connections specialised requirements enquiry support gmail",
    q: "What if none of the plans suit my needs?",
    a: (
      <div className="space-y-3">
        <p>If your automation requirements don't fit within the Essential, Advanced or Pro plans, we can create a custom plan tailored to your needs.</p>
        <p>Custom plans may be suitable for businesses that require more steps, app connections or specialised automation requirements than our standard plans provide.</p>
        <p>Contact DISJINX through our contact page or at disjinx.support@gmail.com to discuss your requirements.</p>
      </div>
    ),
  },
  {
    cat: "plans",
    t: "free trial one week simple custom automation runs free no charge commitment enquire",
    q: "Does DISJINX offer a free trial?",
    a: (
      <div className="space-y-3">
        <p>Yes. We offer a free one-week trial where we build a simple, custom automation for you and allow you to use it for one week.</p>
        <p>If you're interested in our free trial, contact us at disjinx.support@gmail.com or through our contact form at disjinx.com/contact.</p>
      </div>
    ),
  },
  {
    cat: "plans",
    t: "move free trial automation paid plan migrate build further add steps keep as is",
    q: "Can I move my free trial automation over to a paid plan?",
    a: "Yes! If you enjoyed how things went with your free trial automation, you can request to have it moved over to a paid plan after the free trial ends. We can keep it exactly how it is or even build it further and add more steps.",
  },
  {
    cat: "billing",
    t: "purchase process how to buy checkout setup fee charged consultation planning email build testing launch weekly subscription begins ongoing support maintenance",
    q: "How does the purchase process work at DISJINX?",
    a: (
      <div className="space-y-4">
        <ol className="list-decimal space-y-2 pl-5">
          <li><strong className="font-semibold text-neutral-950">Purchase your plan</strong> — Once you've decided which plan is right for you, select it on our website at disjinx.com and complete your purchase. Only the one-time setup fee is charged at this stage.</li>
          <li><strong className="font-semibold text-neutral-950">Consultation &amp; planning</strong> — After your purchase, we'll contact you via email to discuss your business, what you'd like to automate and your specific requirements. We'll then determine the best approach for your automation.</li>
          <li><strong className="font-semibold text-neutral-950">Build &amp; testing</strong> — Once we've agreed on the automation, we'll begin building and testing it. From the moment development begins, your setup fee is no longer refundable.</li>
          <li><strong className="font-semibold text-neutral-950">Launch</strong> — Once your automation has been successfully built and tested, we'll notify you and implement it into your workflow. Your weekly subscription begins when your automation is launched and switched on.</li>
          <li><strong className="font-semibold text-neutral-950">Maintain</strong> — We'll continue to maintain and monitor your automation according to your plan.</li>
        </ol>
        <p>Need more than one automation? You can purchase multiple plans under the same account. Each additional plan follows the same process.</p>
      </div>
    ),
  },
  {
    cat: "billing",
    t: "usage meaning credits resources required automation run length complexity actions services how often measured weekly credit count my billing",
    q: "What does “usage” mean? What are credits?",
    a: (
      <div className="space-y-3">
        <p>Usage refers to the resources required for your automation to run. Usage can vary depending on the length and complexity of your automation, the actions it performs, the services it connects to and how often it runs.</p>
        <p>Usage at DISJINX is measured in 'credits', and you can see your weekly credit count under 'My Billing'.</p>
      </div>
    ),
  },
  {
    cat: "billing",
    t: "usage fees cost varies build run frequency rate plan credit monitor my billing expected usage explained before launch",
    q: "How much will my usage fees cost?",
    a: (
      <div className="space-y-3">
        <p>Usage fees vary depending on how your automation is built and how frequently it runs. Because every business uses its automations differently, we can't provide a fixed usage cost in advance.</p>
        <p>Your applicable usage rate is determined by your plan, and you can monitor your credit usage through My Billing.</p>
        <p>Upon request, we'll also help you understand the expected usage of your automation before it is launched.</p>
      </div>
    ),
  },
  {
    cat: "billing",
    t: "fail pay weekly subscription missed late notification email 24 hours automation switched off reactivated account up to date",
    q: "What happens if I don't pay my weekly subscription?",
    a: (
      <div className="space-y-3">
        <p>If we don't receive your weekly subscription payment, we'll send you a notification via email. You'll have 24 hours to make the outstanding payment. If payment is still not received after 24 hours, your automation will be switched off.</p>
        <p>Your automation will not be deleted, so it can be reactivated once your account is brought up to date.</p>
      </div>
    ),
  },
  {
    cat: "billing",
    t: "cancellation fees cancel subscription any time no fee",
    q: "Are there any cancellation fees?",
    a: "No. You can cancel your subscription at any time with no cancellation fee.",
  },
  {
    cat: "billing",
    t: "what happens automation cancel choose immediately end weekly period usage still charged kept 90 days reactivation",
    q: "What happens to my automation when I cancel?",
    a: (
      <div className="space-y-3">
        <p>When you cancel, you can choose to turn your automation off immediately or at the end of your current weekly billing period.</p>
        <p>Any usage incurred before your subscription ends will still be charged at the applicable usage rate.</p>
        <p>We will keep your automation in our database for up to 90 days in case you want to reactivate it later.</p>
      </div>
    ),
  },
  {
    cat: "contact",
    t: "contact email support disjinx.support@gmail.com form reach us questions enquiries",
    q: "How can I contact DISJINX?",
    a: "For questions, enquiries or support, contact us at disjinx.support@gmail.com or through our contact page (at disjinx.com/contact).",
  },
];

const FaqAccordion = ({ items, idPrefix }) => (
  <Accordion type="single" collapsible data-testid={`faq-accordion-${idPrefix}`} className="border-t border-neutral-200">
    {items.map((f, i) => (
      <AccordionItem key={`${idPrefix}-${i}`} value={`${idPrefix}-${i}`} data-testid={`faq-item-${idPrefix}-${i}`} className="border-b border-neutral-200">
        <AccordionTrigger
          data-testid={`faq-trigger-${idPrefix}-${i}`}
          className="py-7 text-left font-display text-lg font-bold tracking-tight hover:no-underline lg:text-xl"
        >
          <span className="flex items-baseline gap-6">
            <span className="text-sm font-medium text-neutral-500">{String(i + 1).padStart(2, "0")}</span>
            {f.q}
          </span>
        </AccordionTrigger>
        <AccordionContent data-testid={`faq-content-${idPrefix}-${i}`} className="pb-7 pl-12 text-base leading-relaxed text-neutral-600 lg:pl-14">
          {f.a}
        </AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export default function Faq() {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState("all");
  const [aiIndexes, setAiIndexes] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const activeCategories = useMemo(
    () => CATEGORIES.map((c) => ({ ...c, items: faqs.filter((f) => f.cat === c.slug) })).filter((c) => c.items.length > 0),
    []
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return faqs.filter((f) => `${f.q} ${f.t}`.toLowerCase().includes(q));
  }, [query]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setAiIndexes(null);
      setAiLoading(false);
      return;
    }
    let stale = false;
    setAiLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/faq/search`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query: q }),
        });
        const data = await res.json();
        if (!stale && res.ok) setAiIndexes((data.indexes || []).filter((i) => faqs[i]));
      } catch {
        if (!stale) setAiIndexes(null);
      } finally {
        if (!stale) setAiLoading(false);
      }
    }, 600);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [query]);

  const displayItems = results === null ? null : aiIndexes !== null ? aiIndexes.map((i) => faqs[i]) : results;

  const visibleCategories = active === "all" ? activeCategories : activeCategories.filter((c) => c.slug === active);
  return (
    <main data-testid="faq-page" className="pt-40 pb-32 lg:pt-52">
      <Seo
        title="FAQ | AI Automation for Australian Businesses | DISJINX"
        description="Answers about AI automation for Australian businesses: DISJINX plans, weekly pricing from $49/week, setup fees, usage allowances, free one-week trials and how it all works."
        path="/faq"
      />
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-8 text-xs font-semibold uppercase tracking-[0.25em] text-neutral-500"
        >
          Frequently asked questions
        </motion.p>
        <h1 data-testid="faq-headline" className="font-display text-5xl font-extrabold leading-[1.02] tracking-tight lg:text-6xl">
          <MaskedLine delay={0.15}>Answers, before</MaskedLine>
          <MaskedLine delay={0.28} className="text-neutral-500">you even ask.</MaskedLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="mt-20 grid gap-16 lg:grid-cols-12"
        >
          <div className="lg:col-span-8">
            <div className="relative">
              <Search className="absolute left-0 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-400" />
              <input
                data-testid="faq-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search answers — try “pricing” or “automation”…"
                aria-label="Search frequently asked questions"
                className="w-full border-b border-neutral-300 bg-transparent py-4 pl-9 pr-10 text-base outline-none transition-colors duration-200 placeholder:text-neutral-500 focus:border-neutral-950"
              />
              {query && (
                <button
                  data-testid="faq-search-clear"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-neutral-400 transition-colors duration-200 hover:text-neutral-950"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            {!results && (
              <div data-testid="faq-categories" className="mt-8 flex flex-wrap gap-2">
                <button
                  data-testid="faq-category-tab-all"
                  onClick={() => setActive("all")}
                  className={`border px-4 py-2 text-xs font-semibold uppercase tracking-widest transition-colors duration-200 ${active === "all" ? "border-neutral-950 bg-neutral-950 text-white" : "border-neutral-300 text-neutral-600 hover:border-neutral-950 hover:text-neutral-950"}`}
                >
                  All
                </button>
                {activeCategories.map((c) => (
                  <button
                    key={c.slug}
                    data-testid={`faq-category-tab-${c.slug}`}
                    onClick={() => setActive(c.slug)}
                    className={`border px-4 py-2 text-xs font-semibold uppercase tracking-widest transition-colors duration-200 ${active === c.slug ? "border-neutral-950 bg-neutral-950 text-white" : "border-neutral-300 text-neutral-600 hover:border-neutral-950 hover:text-neutral-950"}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            )}

            {results ? (
              <div className="mt-10">
                {displayItems.length === 0 ? (
                  aiLoading ? (
                    <div data-testid="faq-ai-loading" className="flex items-center gap-3 border border-neutral-200 bg-neutral-50 p-10 text-sm text-neutral-600">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      AI is matching your question to the FAQs…
                    </div>
                  ) : (
                    <div data-testid="faq-no-results" className="border border-neutral-200 bg-neutral-50 p-10">
                      <p className="font-display text-xl font-bold tracking-tight">No answers for “{query}”</p>
                      <p className="mt-3 text-sm text-neutral-600">
                        Try different words — or ask our AI assistant in the chat bubble, or send us an enquiry.
                      </p>
                    </div>
                  )
                ) : (
                  <>
                    <p data-testid="faq-search-results-count" className="mb-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-neutral-500">
                      {aiIndexes !== null ? (
                        <>
                          <Sparkles className="h-3.5 w-3.5" />
                          AI-matched · {displayItems.length} {displayItems.length === 1 ? "answer" : "answers"}
                        </>
                      ) : (
                        <>
                          {results.length} {results.length === 1 ? "answer" : "answers"} found
                          {aiLoading && (
                            <span className="flex items-center gap-1.5 normal-case tracking-normal text-neutral-300">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              AI refining…
                            </span>
                          )}
                        </>
                      )}
                    </p>
                    <FaqAccordion items={displayItems} idPrefix="search" />
                  </>
                )}
              </div>
            ) : (
              <div className="mt-12 space-y-16">
                {visibleCategories.map((c) => (
                  <section key={c.slug} data-testid={`faq-category-section-${c.slug}`}>
                    <div className="mb-6 flex items-baseline gap-3">
                      <h2 className="text-xs font-semibold uppercase tracking-[0.25em] text-neutral-500">{c.label}</h2>
                      <span className="text-xs text-neutral-300">{c.items.length}</span>
                    </div>
                    <FaqAccordion items={c.items} idPrefix={c.slug} />
                  </section>
                ))}
              </div>
            )}
          </div>
          <div className="lg:col-span-4">
            <div className="border border-neutral-200 bg-neutral-50 p-10">
              <h2 className="font-display text-2xl font-bold tracking-tight">Still curious?</h2>
              <p className="mt-4 text-sm leading-relaxed text-neutral-600">
                Ask our AI assistant in the chat bubble — or send us an enquiry and a human will get back to you.
              </p>
              <Link
                to="/contact"
                data-testid="faq-contact-cta"
                className="group mt-8 inline-flex items-center gap-2 bg-neutral-950 px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-800"
              >
                Contact us
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
